import React, { ReactNode } from 'react';
import cx from 'clsx';
import { useTranslation } from 'react-i18next';

import { Button, ButtonProps } from '../../Button';
import { wrapModalPromise, WrapModalProps } from '../wrap';
import style from './index.module.scss';

type ConfirmModalProps = {
  title?: ReactNode | string;
  content?: ReactNode;
  showCancelBtn?: boolean;
  cancelBtnText?: string;
  confirmBtnText?: string;
  onConfirmClick?(): void;
  onCancelClick?(): void;
  modalClassName?: string;
  closeable?: boolean;
  cancelBtnGhost?: boolean;
  cancelBtnType?: ButtonProps['type'];
};

export const ConfirmModal = ({
  title,
  content,
  showCancelBtn = true,
  cancelBtnText,
  confirmBtnText,
  cancelBtnGhost = true,
  cancelBtnType = 'gray',
  modalClassName,
  onConfirmClick,
  onCancelClick,
  hide,
  onResolve,
  onReject,
}: ConfirmModalProps & WrapModalProps) => {
  const { t } = useTranslation();
  const handleCancelClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onCancelClick?.();
    hide?.();
  };

  const handleConfirmClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onConfirmClick?.();
    onResolve?.();
    hide?.();
  };

  return (
    <div className={cx(style.confirmModal, modalClassName)}>
      {title && <div className={style.confirmModalTitle}>{title}</div>}
      {content && <div className={style.confirmModalContent}>{content}</div>}
      <div className={style.confirmModalBtns}>
        {showCancelBtn ? (
          <Button
            type={cancelBtnType}
            ghost={cancelBtnGhost}
            onClick={handleCancelClick}
            className={style.confirmModalBtn}>
            {cancelBtnText || t('Modal.cancel')}
          </Button>
        ) : null}
        <Button type="primary" onClick={handleConfirmClick} className={style.confirmModalBtn}>
          {confirmBtnText || t('Modal.confirm')}
        </Button>
      </div>
    </div>
  );
};

export const confirm = wrapModalPromise<ConfirmModalProps, void>(ConfirmModal);
